// Authentication storage for caching user information in IndexedDB

import { openDatabase, putToStore, getFromStore, deleteFromStore, STORES } from './db-core';

// Extend STORES to include auth store
const AUTH_STORE = 'cachedUser';
const AUTH_STORE_KEY = 'current';

interface CachedUser {
    id: string;
    name: string;
    email: string;
    roles: string[];
    cachedAt: string;
}

// Ensure the auth store exists (needs DB upgrade)
async function ensureAuthStore(): Promise<void> {
    const db = await openDatabase();
    // Store will be created in db-core upgrade handler
    return;
}

/**
 * Cache user information in IndexedDB for offline access.
 */
async function cacheUser(user: CachedUser): Promise<void> {
    try {
        const db = await openDatabase();
        const transaction = db.transaction(AUTH_STORE, 'readwrite');
        const store = transaction.objectStore(AUTH_STORE);
        
        // Store with a fixed key so we always have one cached user
        const record = { key: AUTH_STORE_KEY, ...user };
        await new Promise<void>((resolve, reject) => {
            const request = store.put(record);
            request.onsuccess = () => resolve();
            request.onerror = () => reject(request.error);
        });
        
        console.log('[AuthStorage] User cached:', user.name);
    } catch (error) {
        console.error('[AuthStorage] Failed to cache user:', error);
        throw error;
    }
}

/**
 * Get the cached user from IndexedDB.
 */
async function getCachedUser(): Promise<CachedUser | null> {
    try {
        const db = await openDatabase();
        const transaction = db.transaction(AUTH_STORE, 'readonly');
        const store = transaction.objectStore(AUTH_STORE);
        
        const result = await new Promise<CachedUser | null>((resolve, reject) => {
            const request = store.get(AUTH_STORE_KEY);
            request.onsuccess = () => {
                const record = request.result;
                if (record) {
                    // Remove the key property before returning
                    const { key, ...user } = record;
                    resolve(user as CachedUser);
                } else {
                    resolve(null);
                }
            };
            request.onerror = () => reject(request.error);
        });
        
        if (result) {
            console.log('[AuthStorage] Loaded cached user:', result.name);
        } else {
            console.log('[AuthStorage] No cached user found');
        }
        
        return result;
    } catch (error) {
        console.error('[AuthStorage] Failed to get cached user:', error);
        return null;
    }
}

/**
 * Clear the cached user from IndexedDB (on sign-out).
 */
async function clearCachedUser(): Promise<void> {
    try {
        const db = await openDatabase();
        const transaction = db.transaction(AUTH_STORE, 'readwrite');
        const store = transaction.objectStore(AUTH_STORE);
        
        await new Promise<void>((resolve, reject) => {
            const request = store.delete(AUTH_STORE_KEY);
            request.onsuccess = () => resolve();
            request.onerror = () => reject(request.error);
        });
        
        console.log('[AuthStorage] Cached user cleared');
    } catch (error) {
        console.error('[AuthStorage] Failed to clear cached user:', error);
        throw error;
    }
}

export const authStorage = {
    cacheUser,
    getCachedUser,
    clearCachedUser
};
